import React, { useState, useEffect } from "react";
import { getAllRates, setCustomerRate, deleteRate, calculateCost } from "../lib/rates";
import { getAllCustomers } from "../lib/users";
import { WAREHOUSE_LIST, UNITS } from "../lib/warehouses";
import { Modal, toast, formatDate } from "./UI";

export default function ShippingRateModal({ onClose }) {
  const [customers,  setCustomers]  = useState([]);
  const [rates,      setRates]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selEmail,   setSelEmail]   = useState("");
  const [selWH,      setSelWH]      = useState("USA");
  const [selUnit,    setSelUnit]    = useState("7000");
  const [amount,     setAmount]     = useState("");
  const [saving,     setSaving]     = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [custs, rts] = await Promise.all([getAllCustomers(), getAllRates()]);
      setCustomers(custs); setRates(rts);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const getRate = (email, warehouse, unit) => {
    return rates.find(r => r.email === email.toLowerCase() && r.warehouse === warehouse && r.unit === unit);
  };

  const save = async () => {
    if (!selEmail || !amount) { toast("Select customer and enter amount.", "error"); return; }
    setSaving(true);
    try {
      await setCustomerRate(selEmail, selWH, selUnit, amount);
      toast("Rate saved!");
      setAmount("");
      load();
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Manage shipping rates" onClose={onClose} maxWidth={600}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
        Set rates per kg for each customer, warehouse, and unit type.
      </p>

      {/* Set rate form */}
      <div style={{ background: "var(--bg-soft)", borderRadius: "var(--radius)", padding: 14, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Set rate</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <select value={selEmail} onChange={e => setSelEmail(e.target.value)} style={{ flex: 2, minWidth: 180 }}>
            <option value="">Select customer...</option>
            {customers.map(c => <option key={c.uid} value={c.email}>{c.name} ({c.email})</option>)}
          </select>
          <select value={selWH} onChange={e => setSelWH(e.target.value)} style={{ width: "auto" }}>
            {WAREHOUSE_LIST.map(w => <option key={w.id} value={w.id}>{w.flag} {w.label}</option>)}
          </select>
          <select value={selUnit} onChange={e => setSelUnit(e.target.value)} style={{ width: "auto" }}>
            {Object.entries(UNITS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="number" step="0.5" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Rate per kg ($)" style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "..." : "Save"}</button>
        </div>
      </div>

      {/* Rates table grouped by customer */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Current rates</div>
      {loading ? <div style={{ textAlign: "center", padding: "1rem", color: "var(--text-muted)" }}>Loading...</div> : (
        <div style={{ border: "0.5px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
          {customers.map((c, i) => {
            const custRates = rates.filter(r => r.email === c.email.toLowerCase());
            if (custRates.length === 0) return (
              <div key={c.uid} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: i < customers.length - 1 ? "0.5px solid var(--border)" : "none", background: "var(--bg-soft)" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.email}</div>
                </div>
                <span style={{ fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }}>No rates set</span>
              </div>
            );
            return (
              <div key={c.uid} style={{ borderBottom: i < customers.length - 1 ? "0.5px solid var(--border)" : "none" }}>
                <div style={{ padding: "8px 14px", background: "var(--bg-soft)", fontSize: 13, fontWeight: 600 }}>
                  {c.name} <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>— {c.email}</span>
                </div>
                {custRates.map(r => (
                  <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 14px 6px 24px", borderTop: "0.5px solid var(--border)" }}>
                    <span style={{ fontSize: 12 }}>{WAREHOUSE_LIST.find(w => w.id === r.warehouse)?.flag} {r.warehouse}</span>
                    <span style={{ fontSize: 12, background: "var(--bg-tertiary)", padding: "2px 8px", borderRadius: 10 }}>{UNITS[r.unit]?.label || r.unit}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 14, fontWeight: 700, color: "var(--green-dark)" }}>${r.ratePerKg}/kg</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setSelEmail(r.email); setSelWH(r.warehouse); setSelUnit(r.unit); setAmount(r.ratePerKg); }}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={async () => { if (window.confirm("Remove?")) { await deleteRate(r.id); load(); } }}>Remove</button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <div className="modal-footer" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}
